import { createFileRoute } from '@tanstack/react-router';
import type { IconName } from 'lucide-react/dynamic';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { Form, FormContent, FormFooter } from '@/components/ui/form';
import { Icon } from '@/components/ui/icon';
import { ContentLoader } from '@/components/ui/loaders';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { ScreenForm } from '@/lib/screen-renderer';
import { useDynamicCreate } from '@/services/dynamic-resource';
import { useScreenByResource } from '@/services/screens';
import type { ValidationError } from '@/types/api/validation-error';

export const Route = createFileRoute('/_app/$resource/create')({
  head: () => meta({ label: 'Novo' }),
  component: RouteComponent,
});

function RouteComponent() {
  const { resource } = Route.useParams();
  const router = useRouter();

  const { data: screen, isLoading: loadingScreen } = useScreenByResource(resource);
  const {
    mutate: save,
    error,
    isSuccess,
    isPending: isSubmitting,
  } = useDynamicCreate(resource);

  const form = useForm<Record<string, any>>({
    shouldUseNativeValidation: false,
    defaultValues: {},
  });

  const handleSubmit = (data: Record<string, any>) => save(data);

  useEffect(() => {
    if (isSuccess) router.back();
  }, [isSuccess, router]);

  useEffect(() => {
    if (error && (error as any).status === 422) {
      const { errors } = (error as any).value as ValidationError<Record<string, any>>;

      if (errors) {
        Object.entries(errors).forEach(([key, message]) => {
          form.setError(key, { message: message as string });
        });
      }
    }
  }, [error, form]);

  if (loadingScreen || !screen) return <ContentLoader show />;

  const label = `Novo(a) ${screen.label}`;

  return (
    <Content
      breadcrumbs={{
        title: label,
        icon: screen.icon as IconName,
        items: [
          { label: 'Listagem', route: `/${resource}` },
          { label: 'Novo', active: true },
        ],
      }}
    >
      <Form
        id="form"
        title={`Dados do ${label}`}
        description={`Informe os dados do ${label.toLowerCase()}.`}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormContent>
          <ScreenForm
            fields={screen.layout.fields}
            control={form.control}
            mode="create"
            resource={resource}
          />
        </FormContent>
        <FormFooter>
          <Button
            type="button"
            disabled={isSubmitting}
            variant="outline"
            onClick={() => form.reset()}
          >
            Limpar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="success"
            form="form"
            className="flex items-center gap-2"
          >
            <Icon name="save" />
            Salvar
          </Button>
        </FormFooter>
      </Form>
    </Content>
  );
}
